import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import Navigation from '../components/common/Navigation';
import Footer from '../components/common/Footer';
import MarketingHero from '../components/marketing/MarketingHero';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    preferredContact: 'email',
  });

  const supportOptions = [
    {
      title: 'Extended support hours',
      description: 'Phone and secure messaging with clear expectations on response times.',
      icon: ClockIcon,
    },
    {
      title: 'Member service team',
      description: 'Local specialists for accounts, lending questions, and digital banking help.',
      icon: UserGroupIcon,
    },
    {
      title: 'Secure communication',
      description: 'Encryption and verification practices that protect what you share with us.',
      icon: ShieldCheckIcon,
    },
  ];

  const contactChannels = [
    {
      method: 'Email support',
      value: 'support@oakvalecu.com',
      href: 'mailto:support@oakvalecu.com',
      icon: EnvelopeIcon,
      description: 'Send us your questions anytime—we reply as quickly as we can.',
      availability: 'Typically within one business day',
    },
    {
      method: 'Secure message',
      value: 'Via online banking',
      to: '/login',
      icon: ChatBubbleLeftRightIcon,
      description: 'Log in and send a secure message from your dashboard.',
      availability: 'Best for account-specific questions',
    },
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
  };

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900">
      <Navigation />

      <MarketingHero
        badge="We are here for members"
        title="We are here to help"
        titleAccent="every step of the way"
        description="Questions about membership, applications, or digital banking? Reach out—we will connect you with the right local specialist."
      />

      <section className="border-b border-stone-200/80 py-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {contactChannels.map((channel) => (
              <div
                key={channel.method}
                className="flex flex-col overflow-hidden rounded-3xl border border-stone-200 bg-white shadow-sm transition hover:shadow-md"
              >
                <div className="border-b border-stone-100 bg-gradient-to-br from-stone-50 to-white p-8">
                  <channel.icon className="h-11 w-11 text-emerald-800" />
                  <h3 className="mt-6 font-serif text-xl font-bold text-stone-900 sm:text-2xl">{channel.method}</h3>
                  <p className="mt-3 text-stone-600">{channel.description}</p>
                  {channel.to ? (
                    <Link
                      to={channel.to}
                      className="mt-6 inline-block rounded-full border border-emerald-200/80 bg-emerald-50 px-4 py-1.5 text-sm font-semibold text-emerald-900 transition hover:bg-emerald-100"
                    >
                      {channel.value}
                    </Link>
                  ) : (
                    <a
                      href={channel.href}
                      className="mt-6 inline-block rounded-full border border-emerald-200/80 bg-emerald-50 px-4 py-1.5 text-sm font-semibold text-emerald-900 transition hover:bg-emerald-100"
                    >
                      {channel.value}
                    </a>
                  )}
                </div>
                <div className="bg-stone-50/80 px-8 py-5">
                  <p className="text-sm text-stone-600">{channel.availability}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 rounded-3xl border border-stone-200 bg-white p-8 shadow-sm md:text-left">
            <h3 className="text-lg font-semibold text-stone-900">Main office</h3>
            <p className="mt-2 text-stone-600">6255 Carrsville Hwy, Carrsville, VA 23315</p>
            <p className="mt-3 text-sm text-stone-500">
              Federally insured by NCUA. Oakvale Credit Union is an equal opportunity lender. NMLS ID: 6488745.
            </p>
          </div>
        </div>
      </section>

      <section className="border-b border-stone-200/80 bg-white py-20">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="font-serif text-3xl font-bold text-stone-900 md:text-4xl">Send us a message</h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-stone-600">
              Share a few details and we will route your note to the right team. For sensitive account issues, sign in
              and use secure messaging when possible.
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="rounded-3xl border border-stone-200 bg-gradient-to-br from-stone-50 to-white p-8 shadow-sm md:p-12"
          >
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-stone-700" htmlFor="contact-name">
                  Name
                </label>
                <input
                  id="contact-name"
                  type="text"
                  className="w-full rounded-2xl border border-stone-200 bg-white px-4 py-3 text-stone-900 transition focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-600/25"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-stone-700" htmlFor="contact-email">
                  Email
                </label>
                <input
                  id="contact-email"
                  type="email"
                  className="w-full rounded-2xl border border-stone-200 bg-white px-4 py-3 text-stone-900 transition focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-600/25"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="mt-8">
              <label className="mb-2 block text-sm font-medium text-stone-700" htmlFor="contact-subject">
                Subject
              </label>
              <select
                id="contact-subject"
                className="w-full rounded-2xl border border-stone-200 bg-white px-4 py-3 text-stone-900 transition focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-600/25"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                required
              >
                <option value="">Select a subject</option>
                <option value="general">General inquiry</option>
                <option value="account">Account support</option>
                <option value="loan">Loan information</option>
                <option value="technical">Technical support</option>
              </select>
            </div>

            <div className="mt-8">
              <label className="mb-2 block text-sm font-medium text-stone-700" htmlFor="contact-message">
                Message
              </label>
              <textarea
                id="contact-message"
                className="w-full rounded-2xl border border-stone-200 bg-white px-4 py-3 text-stone-900 transition focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-600/25"
                rows={4}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                required
              />
            </div>

            <div className="mt-8">
              <span className="mb-3 block text-sm font-medium text-stone-700">Preferred contact method</span>
              <div className="flex flex-wrap gap-6">
                <label className="flex cursor-pointer items-center">
                  <input
                    type="radio"
                    name="contact"
                    value="email"
                    checked={formData.preferredContact === 'email'}
                    onChange={(e) => setFormData({ ...formData, preferredContact: e.target.value })}
                    className="text-emerald-700 focus:ring-emerald-600/30"
                  />
                  <span className="ml-2 text-stone-700">Email</span>
                </label>
                <label className="flex cursor-pointer items-center">
                  <input
                    type="radio"
                    name="contact"
                    value="phone"
                    checked={formData.preferredContact === 'phone'}
                    onChange={(e) => setFormData({ ...formData, preferredContact: e.target.value })}
                    className="text-emerald-700 focus:ring-emerald-600/30"
                  />
                  <span className="ml-2 text-stone-700">Phone</span>
                </label>
              </div>
            </div>

            <button
              type="submit"
              className="mt-10 inline-flex w-full items-center justify-center rounded-2xl bg-emerald-800 px-6 py-3.5 text-base font-semibold text-white shadow-sm transition hover:bg-emerald-900 focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:ring-offset-2"
            >
              Send message
              <ArrowRightIcon className="ml-2 h-5 w-5" />
            </button>
          </form>
        </div>
      </section>

      <section className="py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {supportOptions.map((option) => (
              <div
                key={option.title}
                className="rounded-3xl border border-stone-200 bg-white p-8 shadow-sm transition hover:shadow-md"
              >
                <div className="inline-flex rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
                  <option.icon className="h-8 w-8 text-emerald-800" />
                </div>
                <h3 className="mt-6 text-xl font-bold text-stone-900">{option.title}</h3>
                <p className="mt-3 text-stone-600">{option.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
